import React, { useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Link,
} from '@mui/material';
import { Login as LoginIcon } from '@mui/icons-material';
import { useMsal } from '@azure/msal-react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '@horwath-htl/shared-branding';
import { loginRequest } from '../authConfig';
import { CookieConsent } from '../components/CookieConsent';
import { clearEnvironmentStorage, getCurrentEnvironment } from '../utils/environment';
import { performCompleteMsalCleanup, diagnoseMsalState } from '../utils/msalCleanup';

export const LoginPage: React.FC = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const [interactionError, setInteractionError] = React.useState<string | null>(null);
  const [isClearing, setIsClearing] = React.useState(false);

  useEffect(() => {
    if (accounts.length > 0) {
      navigate('/dashboard');
    }
  }, [accounts, navigate]);

  // Check for stuck interaction state on component mount
  useEffect(() => {
    const diagnosis = diagnoseMsalState();
    if (diagnosis.hasStuckInteraction) {
      setInteractionError('Authentication is stuck in progress state. This can happen when switching between environments.');
      console.log('Stuck MSAL interaction detected on page load:', diagnosis.stuckKeys);
    }
  }, []);

  const handleLogin = async () => {
    try {
      // Clear any previous error states
      setInteractionError(null);

      // Check for stuck interaction before attempting login
      const diagnosis = diagnoseMsalState();
      if (diagnosis.hasStuckInteraction) {
        console.warn('Stuck interaction detected before login attempt:', diagnosis.stuckKeys);
        setInteractionError('Authentication is stuck. Click "Clear Authentication State" below to fix this.');
        return;
      }

      // Attempt login
      await instance.loginRedirect(loginRequest);
    } catch (error: any) {
      console.error('Login failed:', error);

      // Handle specific interaction_in_progress error
      if (error.errorCode === 'interaction_in_progress' ||
          error.message?.includes('interaction_in_progress') ||
          error.errorCode === 'BrowserAuthError' ||
          error.name === 'BrowserAuthError') {
        setInteractionError('Authentication is stuck. This often happens when switching between environments. Click "Clear Authentication State" below to fix this.');
        console.warn('Interaction in progress error detected');
      } else {
        // Handle other login errors
        setInteractionError(`Login failed: ${error.message || error.errorCode || 'Unknown error'}`);
      }
    }
  };

  const handleClearAuthState = async () => {
    try {
      setIsClearing(true);
      setInteractionError(null);

      console.log('Clearing stuck authentication state...');

      // Perform complete MSAL cleanup
      await performCompleteMsalCleanup(instance);

      // Clear environment-specific storage
      clearEnvironmentStorage();

      console.log('Authentication state cleared. Reloading page to reset MSAL instance...');

      // Force reload the page to completely reset MSAL instance
      // This is the most reliable way to clear in-memory interaction state
      setTimeout(() => {
        window.location.reload();
      }, 100);

    } catch (error) {
      console.error('Failed to clear authentication state:', error);
      // Force reload as fallback
      window.location.reload();
    }
  };

  const handleNuclearClear = () => {
    try {
      console.log('Performing nuclear clear - removing ALL storage...');

      // Clear absolutely everything
      localStorage.clear();
      sessionStorage.clear();

      // Clear all cookies
      document.cookie.split(";").forEach((c) => {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });

      console.log('All storage cleared. Reloading page...');

      // Force reload the page to get a clean state
      window.location.reload();
    } catch (error) {
      console.error('Nuclear clear failed:', error);
      // Force reload as last resort
      window.location.reload();
    }
  };

  const handleLogoutAll = async () => {
    try {
      const currentEnv = getCurrentEnvironment();
      console.log(`Clearing all authentication data for ${currentEnv.displayName} environment...`);
      
      // Use the comprehensive cleanup utility
      await performCompleteMsalCleanup(instance);
      
      // Clear environment-specific data as well
      clearEnvironmentStorage();
      
      // Clear any error states
      setInteractionError(null);
      
      // Try to logout from MSAL if there are accounts
      if (accounts.length > 0) {
        console.log(`Logging out ${accounts.length} MSAL accounts...`);
        await instance.logoutRedirect({
          postLogoutRedirectUri: '/login',
        });
      } else {
        // Just reload the page to clear any remaining state
        console.log('No MSAL accounts found, reloading page to clear remaining state...');
        window.location.reload();
      }
    } catch (error) {
      console.error('Logout all failed:', error);
      // Force reload as fallback
      window.location.reload();
    }
  };

  return (
    <>
      <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Card sx={{ textAlign: 'center', p: 4 }}>
        <CardContent>
          <Box sx={{ mb: 4 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Logo height={60} />
            </Box>
          </Box>

          <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
            <Typography variant="body2" gutterBottom>
              This is a secure area for authorized personnel only.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              You will be redirected to Microsoft to authenticate using your corporate account.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please click the button below to proceed with authentication.
            </Typography>
          </Paper>

          {/* Error message display */}
          {interactionError && (
            <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.light', color: 'error.contrastText' }}>
              <Typography variant="body2" gutterBottom>
                Authentication Error
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {interactionError}
              </Typography>
              {(interactionError.includes('stuck') || interactionError.includes('interaction_in_progress') || interactionError.includes('remains')) && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearAuthState}
                    disabled={isClearing}
                    sx={{
                      color: 'error.contrastText',
                      borderColor: 'error.contrastText',
                      '&:hover': {
                        borderColor: 'error.contrastText',
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    {isClearing ? 'Clearing...' : 'Clear Authentication State'}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleNuclearClear}
                    sx={{
                      color: 'error.contrastText',
                      borderColor: 'error.contrastText',
                      '&:hover': {
                        borderColor: 'error.contrastText',
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                      },
                    }}
                  >
                    Clear All Storage
                  </Button>
                </Box>
              )}
            </Paper>
          )}

          <Button
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            onClick={handleLogin}
            disabled={isClearing}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem',
            }}
          >
            {isClearing ? 'Clearing State...' : 'Sign in with Microsoft'}
          </Button>

          <Box sx={{ mt: 4 }}>
            <Typography variant="caption" color="text.secondary">
              By signing in, you agree to our{' '}
              <Link
                href="/eula"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/eula');
                }}
                sx={{ cursor: 'pointer' }}
              >
                End User License Agreement
              </Link>
              {' '}and{' '}
              <Link
                href="/privacy"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/privacy');
                }}
                sx={{ cursor: 'pointer' }}
              >
                Privacy Policy
              </Link>
              .
            </Typography>
          </Box>
        </CardContent>
      </Card>
      
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          © Copyright Barrett HTL Inc {new Date().getFullYear()} - Horwath HTL US Licensee Portal
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
          <Link
            href="/privacy"
            onClick={(e) => {
              e.preventDefault();
              navigate('/privacy');
            }}
            sx={{ 
              cursor: 'pointer',
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            Privacy Policy
          </Link>
          <Typography variant="body2" color="text.secondary">•</Typography>
          <Link
            href="/eula"
            onClick={(e) => {
              e.preventDefault();
              navigate('/eula');
            }}
            sx={{ 
              cursor: 'pointer',
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            End User License Agreement
          </Link>
        </Box>
        
        {/* Troubleshooting link */}
        <Box sx={{ mt: 2 }}>
          <Link
            component="button"
            variant="caption"
            onClick={handleLogoutAll}
            sx={{ 
              color: 'text.secondary',
              textDecoration: 'underline',
              fontSize: '0.75rem',
              '&:hover': {
                color: 'text.primary',
              }
            }}
          >
            Logout of all accounts
          </Link>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
            (Use if having trouble signing in)
          </Typography>
        </Box>
      </Box>
    </Container>
    <CookieConsent />
    </>
  );
};
